import { IStoreState } from '../types';
import { createSelector } from 'reselect';
import { find, matches, chain, get } from 'lodash';
import { getIsNewDesignEnabled } from './user';
import { ITemplate } from '../reducers';

export const getTemplatesList = (state: IStoreState) => state.templates.items;

/** The new design shows a template's `newDisplayName` where it has one. */
export const getTemplateTitle = (
  template: { displayName: string; newDisplayName?: string },
  isNewDesign: boolean
): string => (isNewDesign && template.newDisplayName) || template.displayName;

/**
 * Template descriptions are written with the old title ("The Rose Template…"), so the new
 * design swaps in its own title wherever the old one appears.
 */
export const getTemplateDescription = (
  description: string,
  template: { displayName?: string; newDisplayName?: string },
  isNewDesign: boolean
): string => {
  if (!isNewDesign || !template.displayName || !template.newDisplayName) {
    return description;
  }

  return description.split(template.displayName).join(template.newDisplayName);
};

export const getAreTemplatesFetched = (state: IStoreState) => state.templates.isFetched;
export const getDevelopedTemplates = createSelector([getTemplatesList], (templates) =>
  chain(templates).filter({ inDevelopment: false }).orderBy(['order'], ['asc']).value()
);

// Old-design templates the redesign folded into one (Valencia). The backend hides them from
// new-design users, so a collection still on one of them is marked as its replacement.
const REPLACED_TEMPLATES: Record<string, string> = {
  SpringVibesYellow: 'SpringVibes',
  SpringVibesRose: 'SpringVibes',
  SpringVibesBlue: 'SpringVibes'
};

export const getSelectedTemplate = createSelector(
  getDevelopedTemplates,
  (templates) =>
    (name: string = '', type: string = '') =>
      find(templates, matches({ name, type })) ||
      (REPLACED_TEMPLATES[name] &&
        find(templates, matches({ name: REPLACED_TEMPLATES[name], type }))) ||
      {}
);

/** The same mapping for a stored template `_id` (`ScrAppBook2_SpringVibesRose`), as presets keep. */
export const getReplacementTemplateId = (id: string = ''): string => {
  const separator = id.indexOf('_');
  const replacement = REPLACED_TEMPLATES[id.slice(separator + 1)];

  return separator > 0 && replacement ? `${id.slice(0, separator)}_${replacement}` : id;
};

/**
 * A paid template the backend doesn't allow and the photographer hasn't bought. A free trial is
 * allowed every template, so it can pick premium ones.
 */
export const getIsTemplateLocked = createSelector(
  [(state: IStoreState) => get(state, 'auth.subscription.purchased_templates', []) as string[]],
  (purchasedTemplates) =>
    (template: Pick<ITemplate, 'name' | 'isPurchable' | 'userIsAllowedToUseTemplate'>): boolean => {
      const isBought = purchasedTemplates.includes(template.name);

      return template.isPurchable && !isBought && !template.userIsAllowedToUseTemplate;
    }
);

export const getPaidTemplatesOptions = createSelector(
  [getTemplatesList, getIsNewDesignEnabled],
  (templates, isNewDesign) => (currencySymbol: string) =>
    chain(templates)
      .filter({ isPurchable: true })
      .map((template) => ({
        text: `${getTemplateTitle(template, isNewDesign)} - ${template.price}${currencySymbol}`,
        value: template._id
      }))
      .value()
);

/** Template prices come with Stripe's product name; the new design shows the template's own title. */
export const getTemplateTitleById = createSelector(
  [getTemplatesList, getIsNewDesignEnabled],
  (templates, isNewDesign) => (templateId: string, fallback: string) => {
    const template = find(templates, { _id: templateId });

    return template ? getTemplateTitle(template, isNewDesign) : fallback;
  }
);

/**
 * Invoice items carry Stripe's product names joined by commas ("Calm Template, Professional Plan").
 * Invoices issued before the products were renamed use the old title, which the backend built
 * without the "Vicky Baumann" prefix; the new design shows the template's own title there.
 */
export const getInvoiceProductTitle = createSelector(
  [getTemplatesList, getIsNewDesignEnabled],
  (templates, isNewDesign) => (productName: string) => {
    if (!isNewDesign || !productName) {
      return productName;
    }

    return productName
      .split(', ')
      .map((item) => {
        const template = find(
          templates,
          ({ displayName, newDisplayName }) =>
            Boolean(newDisplayName) &&
            item === `${displayName.replace('Vicky Baumann', '').trim()} Template`
        );

        return template ? `${template.newDisplayName} Template` : item;
      })
      .join(', ');
  }
);

export const getTemplatesFonts = (state: IStoreState) => state.templates.fonts;
